import * as React from 'react'

import { useQuery } from '@apollo/client'
import { Box, Button, Text } from '@factorialco/deprecated-design-system'
import { Form } from 'factorial-form'
import { FetchEmployeeIncidencesDocument } from 'generated/resources/graphql'
import xor from 'lodash/xor'
import xorBy from 'lodash/xorBy'

import { observer } from 'mobx-react'

import EmployeeSelector from 'modules/core/components/EmployeeSelector'
import Modal from 'modules/core/components/Modal'
import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import { useEffect } from 'modules/core/hooks/useEffect'
import asyncAction from 'modules/core/lib/asyncAction'
import i18n from 'modules/core/lib/i18n'

import type { ContractVersions } from 'modules/contracts/store/collections/contractVersions'
import employees from 'modules/employees/store/collections/employees'
import AllowanceMapper from 'modules/timeoff/components/TimeoffAllowances/AllowanceMapper'
import { timeoffPolicyActiveEmployees } from 'modules/timeoff/lib/timeoffPolicyEmployees'
import allowances from 'modules/timeoff/store/collections/allowances'
import timeoffPolicies, {
  AllowanceMapItem,
  BulkAssignEmployeesDto,
  employeePolicy,
} from 'modules/timeoff/store/collections/timeoffPolicies/policies'
import type Allowance from 'modules/timeoff/store/models/Allowance'
import type TimeoffPolicy from 'modules/timeoff/store/models/TimeoffPolicy/Policy'

class AddForm extends Form {
  constructor(values: {}) {
    super(values, {
      timeoff_allowance_id: 'number',
    })
  }
}

interface Props {
  timeoffPolicyId: number
  onClose: () => void
  contractVersions: ContractVersions
  loading: boolean
}

const AddEmployee = ({ timeoffPolicyId, onClose, contractVersions, loading }: Props) => {
  const [selectedEmployeeIds, setEmployeesIds] = React.useState<number[]>([])
  const { loading: loadingAllowances } = useAsyncData(() => allowances.fetch(), [])

  const [showEmployeeSelector, setShowEmployeeSelector] = React.useState<boolean>(true)
  const [isSaving, setIsSaving] = React.useState<boolean>(false)
  const [showAllowanceSelector, setShowAllowanceSelector] = React.useState<boolean>(false)
  const [showAdjustmentLossAlert, setShowAdjustmentLossAlert] = React.useState<boolean>(false)

  const {
    data,
    loading: loadingIncidences,
    refetch,
  } = useQuery(FetchEmployeeIncidencesDocument, {
    variables: {
      employeeIds: selectedEmployeeIds,
    },
  })

  const incidences = data?.timeoff.allowanceIncidences || []
  const allowancesWithIncidencesIds = Array.from(
    new Set(incidences.map((i) => i.timeoffAllowanceId))
  )

  const getPolicy = (): TimeoffPolicy => {
    const timeoffPolicy = timeoffPolicies.get(timeoffPolicyId)

    if (!timeoffPolicy) {
      throw Error(`Error getting timeoffPolicies with id = ${timeoffPolicyId}`)
    }

    return timeoffPolicy
  }

  const policy = getPolicy()

  const addForm = React.useRef(
    new AddForm({
      timeoff_allowance_id: policy?.attributes.default_allowance_id,
    })
  ).current

  const activeEmployees = () =>
    xorBy(
      timeoffPolicyActiveEmployees(policy, employees),
      employees.active,
      (employee) => employee.attributes.id
    )

  const setMappingsDestroyValue = () => {
    bulkAssignEmployeeDto.mappings.forEach((mapping: AllowanceMapItem) => {
      if (mapping.newAllowanceId === 0) {
        return (mapping._destroy = true)
      }
    })
  }

  const addEmployees = async () => {
    setIsSaving(true)
    if (allowancesWithIncidences.length === 0) {
      const promises: Array<Promise<unknown> | null | undefined> = selectedEmployeeIds.map((id) => {
        const employee = employees.get(id)

        if (!employee) {
          throw Error(`Error getting employees with id = ${id}`)
        }

        return employee.save({
          timeoff_policy_id: policy?.attributes.id,
          timeoff_allowance_id: +addForm.get('timeoff_allowance_id').value,
        })
      })
      const { success } = await asyncAction(() => Promise.all(promises))
      if (success) {
        onClose()
        window.location.reload()
      }
    } else {
      setMappingsDestroyValue()
      const { success } = await asyncAction(() => {
        return timeoffPolicies.bulkAssignEmployees(bulkAssignEmployeeDto)
      })

      if (success) {
        onClose()
        window.location.reload()
      }
    }
  }

  const onSelectEmployee = (employeeId: number | null | undefined) => {
    employeeId ? setEmployeesIds(xor(selectedEmployeeIds, [employeeId])) : setEmployeesIds([])
    refetch()
  }

  const [currentAllowances, setCurrentAllowances] = React.useState<Allowance[]>([])

  useEffect(() => {
    const innerAllowances: Allowance[] = []

    selectedEmployeeIds.forEach((id) => {
      const employee = employees.get(id)

      if (employee) {
        employeePolicy(employee).allowances.forEach((allowance) => {
          if (!innerAllowances.includes(allowance)) {
            innerAllowances.push(allowance)
          }
        })
      }
    })

    setCurrentAllowances(innerAllowances)
  }, [selectedEmployeeIds.length])

  const onAddEmployees = () => {
    if (allowancesWithIncidences.length === 0) {
      setShowAllowanceSelector(false)
      setShowAdjustmentLossAlert(false)
      return addEmployees()
    } else {
      setShowEmployeeSelector(false)
      setBulkAssignEmployeeDto({
        employeeIds: selectedEmployeeIds,
        mappings: allowanceMappings,
        policyId: timeoffPolicyId,
      })
      return setShowAllowanceSelector(true)
    }
  }

  const allowancesWithIncidences = currentAllowances.filter((allowance) => {
    return allowancesWithIncidencesIds.includes(allowance.attributes.id)
  })

  const allowanceMappings: AllowanceMapItem[] = allowancesWithIncidences.map((a) => ({
    originalAllowanceId: a.id,
    newAllowanceId: 0,
    _destroy: false,
  }))

  const [bulkAssignEmployeeDto, setBulkAssignEmployeeDto] = React.useState<BulkAssignEmployeesDto>({
    employeeIds: selectedEmployeeIds,
    mappings: allowanceMappings,
    policyId: timeoffPolicyId,
  })

  const updateBulkAssignEmployeesDto = (data: BulkAssignEmployeesDto) => {
    setBulkAssignEmployeeDto(data)
  }

  const allAllowancesSelected = (bulkAssignEmployeeDto: BulkAssignEmployeesDto) => {
    return bulkAssignEmployeeDto.mappings.every((mapping: AllowanceMapItem) => {
      return mapping.newAllowanceId !== 0
    })
  }

  const shouldShowAdjustmentLossAlert = () => {
    if (!allAllowancesSelected(bulkAssignEmployeeDto)) {
      setShowEmployeeSelector(false)
      setShowAllowanceSelector(false)
      return setShowAdjustmentLossAlert(true)
    } else {
      addEmployees()
    }
  }

  const keepAdjustments = () => {
    setShowAdjustmentLossAlert(false)
    return setShowAllowanceSelector(true)
  }

  const availableEmployees = activeEmployees()

  let title = ''
  if (showAdjustmentLossAlert) {
    title = i18n.t('settings.timeoff_policies.adjustment_loss_modal.title')
  } else if (showAllowanceSelector) {
    title = i18n.t('settings.timeoff_policies.manual_policy_mapper.reassign')
  } else if (showEmployeeSelector) {
    title = i18n.t('settings.timeoff_policies.add_employees_modal.assign.title')
  }

  let description = ''
  if (showAdjustmentLossAlert) {
    description = i18n.t('settings.timeoff_policies.adjustment_loss_modal.description')
  } else if (showAllowanceSelector) {
    description = ''
  } else if (showEmployeeSelector) {
    description = i18n.t('settings.timeoff_policies.add_employees_modal.assign.description')
  }

  return (
    !loading &&
    !loadingAllowances && (
      <Modal
        illustration="alert"
        title={title}
        description={description}
        onClose={onClose}
        footer={
          <>
            {showEmployeeSelector && (
              <Button onClick={onAddEmployees} disabled={!selectedEmployeeIds.length || isSaving}>
                {i18n.t('settings.timeoff_policies.add_employees_modal.button')}
              </Button>
            )}
            {showAllowanceSelector && (
              <Button
                onClick={shouldShowAdjustmentLossAlert}
                disabled={!selectedEmployeeIds.length || isSaving}
              >
                {i18n.t('settings.timeoff_policies.add_employees_modal.adjustments.button')}
              </Button>
            )}
            {showAdjustmentLossAlert && (
              <Box flexDirection="row" alignItems="center" justifyContent="spaceEvenly" gap="s8">
                <Button type="positive" onClick={keepAdjustments} disabled={isSaving}>
                  {i18n.t('settings.timeoff_policies.adjustment_loss_modal.primary_button')}
                </Button>
                <Button type="negative" onClick={addEmployees} disabled={isSaving}>
                  {i18n.t('settings.timeoff_policies.adjustment_loss_modal.secondary_button')}
                </Button>
              </Box>
            )}
          </>
        }
      >
        <>
          {availableEmployees.length === 0 && employees.active.length > 0 ? (
            <Box textAlign="center">
              <Text.Normal color="grey800">
                {i18n.t('settings.timeoff_policies.add_employees_modal.description')}
              </Text.Normal>
            </Box>
          ) : (
            <>
              {showEmployeeSelector && (
                <EmployeeSelector
                  employees={availableEmployees}
                  onSelectEmployee={onSelectEmployee}
                  onSelectMultiple={setEmployeesIds}
                  selectedEmployeeIds={selectedEmployeeIds}
                  contractVersions={contractVersions}
                />
              )}
              {!loadingIncidences && showAllowanceSelector && (
                <AllowanceMapper
                  policyId={timeoffPolicyId}
                  currentAllowances={allowancesWithIncidences}
                  setBulkAssignEmployeeDto={updateBulkAssignEmployeesDto}
                  bulkAssignEmployeeDto={bulkAssignEmployeeDto}
                />
              )}
            </>
          )}
        </>
      </Modal>
    )
  )
}

export default observer(AddEmployee)
